import { AccountType, BrokerOfRecord, ContactFor } from '@newfront-insurance/account-api';
import { AuthRole } from '@newfront-insurance/auth-api';
import { DefinitionList, DetailCard, Stack, Text, colors, EmployeesRow, SingleObjectSelector, useFuzzy, MenuItemType, Flexbox, Popper, IconButton, Badge, Menu2 } from '@newfront-insurance/core-ui';
import { useProvider } from '@newfront-insurance/react-provision';
import debounce from 'lodash/debounce';
import React, { useCallback } from 'react';
import { A as AdminTRPCProvider } from './trpc-2b4d7b2f.esm.js';
import { IDENTITY_TYPE_LABELS, getBusinessIdentifierLabel } from '@newfront-insurance/account-common';
import { formatDate, getLocalTimezone, DateFormatStyle } from '@newfront-insurance/string-formatters';
import { AccountInvitationStatus } from '@newfront-insurance/users-api';
import * as yup from 'yup';

function buildIdentitiesDefinitions(account, showTitleAsBlock) {
  const {
    businessIdentifier,
    personalIdentities
  } = account;

  if (personalIdentities && personalIdentities.length > 0) {
    return personalIdentities.map(identity => ({
      title: IDENTITY_TYPE_LABELS[identity.type],
      value: showTitleAsBlock ? IDENTITY_TYPE_LABELS[identity.type] : identity.value,
      subValue: showTitleAsBlock ? identity.value : undefined,
      type: 'number'
    }));
  }

  if (businessIdentifier) {
    return [{
      title: getBusinessIdentifierLabel((businessIdentifier === null || businessIdentifier === void 0 ? void 0 : businessIdentifier.countryCode) || 'US') || '',
      value: (businessIdentifier === null || businessIdentifier === void 0 ? void 0 : businessIdentifier.value) || '',
      type: 'number'
    }];
  }

  return [];
}
function isPublicAccount(account) {
  return !('details' in account);
}

const ACCOUNT_TYPE_LABELS = {
  [AccountType.COMMERCIAL]: 'Commercial',
  [AccountType.PERSONAL]: 'Personal'
};
function AccountDefinitions(_ref) {
  let {
    account,
    hideTitle = false,
    showTitleAsBlock = false
  } = _ref;
  const definitionListItems = [{
    type: 'business',
    title: 'Type',
    value: ACCOUNT_TYPE_LABELS[account.type] ?? '-'
  }];
  definitionListItems.push(...buildIdentitiesDefinitions(account, showTitleAsBlock));

  if ('mailingAddress' in account) {
    const {
      mailingAddress
    } = account;
    definitionListItems.push({
      title: 'Mailing address',
      type: 'location',
      value: mailingAddress ? `${mailingAddress.street}, ${mailingAddress.city}` : '-',
      subValue: mailingAddress ? `${mailingAddress.state}, ${mailingAddress.zip}` : undefined
    });
  }

  return /*#__PURE__*/React.createElement(DefinitionList, {
    items: definitionListItems,
    hideTitle: hideTitle
  });
}

function AccountCard(_ref) {
  let {
    account,
    actions,
    isHighlighted,
    defaultExpanded,
    isExpandable = false,
    onCopyEmployeeEmail,
    servicingTeam,
    cardProps,
    selectableOptions,
    badgeProps,
    eyebrowProps,
    isHovering
  } = _ref;
  const dba = isPublicAccount(account) ? account.dba : account.details.dba;
  return /*#__PURE__*/React.createElement(DetailCard, {
    title: account.name,
    subtitle: dba,
    isHighlighted: isHighlighted,
    isHovering: isHovering,
    defaultExpanded: defaultExpanded,
    isExpandable: isExpandable,
    cardProps: cardProps,
    selectableOptions: selectableOptions,
    actions: actions,
    badgeProps: badgeProps,
    eyebrowProps: eyebrowProps
  }, /*#__PURE__*/React.createElement(AccountDefinitions, {
    account: account
  }), servicingTeam && /*#__PURE__*/React.createElement(Stack, {
    direction: "vertical",
    gap: 8
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    color: colors.steel[400]
  }, "Servicing team"), /*#__PURE__*/React.createElement(Stack, {
    direction: "horizontal",
    gap: 4
  }, /*#__PURE__*/React.createElement(EmployeesRow, {
    employees: servicingTeam,
    variant: "expanded",
    onCopyEmail: onCopyEmployeeEmail
  }))));
}

function AccountSwitcher(_ref) {
  let {
    loggedInUserUuid,
    userScopes,
    currentAccountUuid,
    onSelectAccount
  } = _ref;
  const {
    useQuery
  } = useProvider(AdminTRPCProvider);
  const {
    accounts,
    search,
    isLoading
  } = useAccountSwitcherOptions({
    loggedInUserUuid,
    userScopes
  });
  const {
    data: selectedAccount,
    isLoading: isLoadingAccount
  } = useQuery(['accounts.getByUuid', {
    accountUuid: currentAccountUuid
  }]);
  return /*#__PURE__*/React.createElement(SingleObjectSelector, {
    isClearable: false,
    itemToString: account => (account === null || account === void 0 ? void 0 : account.name) ?? '',
    items: accounts.slice(0, 20),
    isLoading: isLoading || isLoadingAccount,
    getItemKey: account => account.uuid,
    onSelect: account => {
      if (account) {
        onSelectAccount(account.uuid);
      }
    },
    selectedItem: selectedAccount ?? null,
    onSearch: search,
    renderItem: (account, _ref2) => {
      let {
        isHovering,
        isSelected
      } = _ref2;
      return /*#__PURE__*/React.createElement(AccountCard, {
        account: account,
        isExpandable: false,
        isHighlighted: isSelected,
        isHovering: isHovering,
        cardProps: {
          hideBorder: true
        }
      });
    },
    renderItemHover: account => /*#__PURE__*/React.createElement("div", {
      style: {
        width: 460
      }
    }, /*#__PURE__*/React.createElement(AccountCard, {
      account: account,
      isExpandable: true,
      defaultExpanded: true,
      cardProps: {
        hideBorder: true
      }
    })),
    renderSelectedItemHover: account => /*#__PURE__*/React.createElement("div", {
      style: {
        width: 460
      }
    }, /*#__PURE__*/React.createElement(AccountCard, {
      account: account,
      isExpandable: true,
      defaultExpanded: true,
      cardProps: {
        hideBorder: true
      }
    }))
  });
}
/**
 * Some user roles can see all accounts on the account switcher because they work across
 * all accounts and are not part of a specific servicing team. i.e. Certificates.
 */

const GLOBAL_SEARCH_ALLOWED_USER_ROLES = [AuthRole.AM_MANAGER, AuthRole.BPO_MANAGER, AuthRole.BPO_WORKER, AuthRole.BPO_TECHTAMMINA, AuthRole.PRODUCER_MANAGER, AuthRole.CSA];

function canUserSearchAllAcounts(scopes) {
  return GLOBAL_SEARCH_ALLOWED_USER_ROLES.some(role => scopes.includes(role));
}

function useAccountSwitcherOptions(_ref3) {
  let {
    loggedInUserUuid,
    userScopes
  } = _ref3;
  const {
    useQuery
  } = useProvider(AdminTRPCProvider);
  const willSearchAllAccounts = canUserSearchAllAcounts(userScopes);
  const {
    data: accounts,
    isLoading: isLoadingAccounts
  } = useQuery(['accounts.getManyPublicAccounts', {
    relatedUserUuid: loggedInUserUuid,
    brokerOfRecord: BrokerOfRecord.NEWFRONT
  }], {
    enabled: !willSearchAllAccounts
  });
  const {
    results: accountsForUser,
    search
  } = useFuzzy(accounts ?? [], {
    keys: ['name', 'dba'],
    debounce: 500
  });
  const [globalSearchQuery, setGlobalSearchQuery] = React.useState('');
  const debouncedGlobalSearch = useCallback(debounce(setGlobalSearchQuery, 500), [setGlobalSearchQuery]);
  const {
    data: allAccounts,
    isLoading: isLoadingAllAccounts
  } = useQuery(['accounts.search', {
    query: globalSearchQuery
  }], {
    enabled: willSearchAllAccounts
  });

  if (willSearchAllAccounts) {
    return {
      accounts: allAccounts ?? [],
      isLoading: isLoadingAllAccounts,
      search: debouncedGlobalSearch
    };
  }

  return {
    accounts: accountsForUser,
    isLoading: isLoadingAccounts,
    search
  };
}

function useTeamMemberMenuItems(_ref) {
  let {
    toggle,
    accountUser,
    onAction,
    hasUniqueAdmin,
    currentUserEmail
  } = _ref;
  const invitationPending = accountUser.invitationStatus.status === AccountInvitationStatus.PENDING;
  const invitationAccepted = accountUser.invitationStatus.status === AccountInvitationStatus.ACCEPTED;
  const invitationCancelled = accountUser.invitationStatus.status === AccountInvitationStatus.CANCELLED;
  const invitationExpired = accountUser.invitationStatus.status === AccountInvitationStatus.EXPIRED;
  const isCurrentUser = !!currentUserEmail && accountUser.email === currentUserEmail;
  const menuItems = [];

  if (invitationPending || invitationExpired || invitationCancelled) {
    menuItems.push({
      type: MenuItemType.LINK,
      label: 'Re-send invite',
      onClick: () => {
        onAction('resend-invitation', accountUser.invitationUuid);
        toggle();
      }
    });
  }

  if (invitationAccepted) {
    const invitationAcceptedNonAdmin = accountUser.isAdmin ? [] : [{
      type: MenuItemType.LINK,
      label: 'Promote to administrator',
      onClick: () => {
        onAction('promote-to-admin', accountUser.teamMemberUuid);
        toggle();
      }
    }];
    const otherInvitationAcceptedOptions = [{
      type: MenuItemType.LINK,
      label: 'Reset password',
      onClick: () => {
        onAction('reset-password', accountUser.teamMemberUuid);
        toggle();
      }
    }, {
      type: MenuItemType.LINK,
      label: 'Edit permissions',
      isDisabled: isCurrentUser,
      onClick: () => {
        onAction('edit', accountUser.teamMemberUuid);
        toggle();
      }
    }];
    menuItems.push(...invitationAcceptedNonAdmin, ...otherInvitationAcceptedOptions);
  }

  if (!invitationCancelled && !invitationExpired) {
    menuItems.push({
      type: MenuItemType.DIVIDER
    });
  }

  if (invitationAccepted) {
    menuItems.push({
      type: MenuItemType.LINK,
      label: 'Remove team member',
      isDisabled: hasUniqueAdmin && accountUser.isAdmin,
      onClick: () => {
        onAction('delete', accountUser.teamMemberUuid);
        toggle();
      }
    });
  }

  if (invitationPending) {
    menuItems.push({
      type: MenuItemType.LINK,
      label: 'Cancel invitation',
      onClick: () => {
        onAction('cancel-invitation', accountUser.invitationUuid);
        toggle();
      }
    });
  }

  return menuItems;
}

const statusToBadgeTypeMap = new Map([[AccountInvitationStatus.ACCEPTED, 'success'], [AccountInvitationStatus.CANCELLED, 'danger'], [AccountInvitationStatus.EXPIRED, 'secondary'], [AccountInvitationStatus.PENDING, 'info']]);
const statusToDisplayName = new Map([[AccountInvitationStatus.ACCEPTED, 'Accepted'], [AccountInvitationStatus.CANCELLED, 'Cancelled'], [AccountInvitationStatus.EXPIRED, 'Expired'], [AccountInvitationStatus.PENDING, 'Pending']]);
function useTeamMemberColumns(_ref) {
  let {
    onAction,
    canManage,
    hasUniqueAdmin,
    currentUserEmail
  } = _ref;
  const columns = [{
    dataField: 'accountUser.firstName',
    label: 'Name',
    format: (_field, accountUser) => /*#__PURE__*/React.createElement(Text, null, accountUser.firstName, " ", accountUser.lastName)
  }, {
    dataField: 'accountUser.email',
    label: 'Email',
    format: (_field, accountUser) => /*#__PURE__*/React.createElement(Text, null, accountUser.email)
  }, {
    dataField: 'accountUser.phone',
    label: 'Phone',
    format: (_field, accountUser) => /*#__PURE__*/React.createElement(Text, null, accountUser.phone || '-')
  }, {
    dataField: 'accountUser.roles',
    label: 'Role',
    format: (_field, accountUser) => {
      const rolesToDisplay = accountUser.groupedRoles.map(role => role.roles.map(role => role.displayName)).flat();
      return /*#__PURE__*/React.createElement(Text, null, rolesToDisplay.join(', '));
    }
  }, {
    dataField: 'accountUser.invitation',
    label: 'Status',
    format: (_field, accountUser) => /*#__PURE__*/React.createElement(Flexbox, {
      flexDirection: "column"
    }, /*#__PURE__*/React.createElement(Badge, {
      type: statusToBadgeTypeMap.get(accountUser.invitationStatus.status)
    }, statusToDisplayName.get(accountUser.invitationStatus.status)), /*#__PURE__*/React.createElement(Text, {
      size: "small",
      color: colors.steel[400]
    }, `Invited on ${formatDate(accountUser.invitationStatus.statusDate, {
      timezone: getLocalTimezone(),
      style: DateFormatStyle.MEDIUM
    })}`))
  }];

  if (canManage) {
    columns.push({
      label: '',
      dataField: 'status',
      sort: false,
      width: '100px',
      format: (_field, accountUser) => {
        return /*#__PURE__*/React.createElement(Flexbox, {
          alignItems: "center",
          gap: 12
        }, /*#__PURE__*/React.createElement(Popper, {
          portalElement: document.body,
          trigger: _ref2 => {
            let {
              toggle,
              triggerRef
            } = _ref2;
            return /*#__PURE__*/React.createElement(IconButton, {
              type: "more",
              innerRef: triggerRef,
              onClick: toggle
            });
          }
        }, _ref3 => {
          let {
            toggle
          } = _ref3;
          return /*#__PURE__*/React.createElement(InviteTeamMembersMenu, {
            toggle: toggle,
            accountUser: accountUser,
            onAction: onAction,
            hasUniqueAdmin: hasUniqueAdmin,
            currentUserEmail: currentUserEmail
          });
        }));
      }
    });
  }

  return columns;
}

function InviteTeamMembersMenu(_ref4) {
  let {
    toggle,
    accountUser,
    onAction,
    hasUniqueAdmin,
    currentUserEmail
  } = _ref4;
  const menuItems = useTeamMemberMenuItems({
    toggle,
    accountUser,
    onAction,
    hasUniqueAdmin,
    currentUserEmail
  });
  return /*#__PURE__*/React.createElement(Menu2, {
    items: menuItems
  });
}

const CORRESPONDENCE_MAPPING_READABLE_NAMES = {
  [ContactFor.ALL]: 'All correspondence',
  [ContactFor.CERTIFICATES]: 'Certificates',
  [ContactFor.INVOICES]: 'Invoices',
  [ContactFor.EXPOSURES]: 'Exposures / submission materials',
  [ContactFor.SIGNATURES]: 'Signatures',
  [ContactFor.CLAIMS]: 'Claims',
  [ContactFor.LOSS_CONTROL]: 'Loss control',
  [ContactFor.PROPOSALS]: 'Proposals'
};
function getHumanReadableCorrespondence(correspondence) {
  const correspondenceString = CORRESPONDENCE_MAPPING_READABLE_NAMES[correspondence];

  if (correspondenceString) {
    return correspondenceString;
  }

  return correspondence;
}

const WEB_URL_REGEXP = // eslint-disable-next-line max-len
/^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i;
const contactValidationSchema = yup.object({
  firstName: yup.string().required().label('First Name'),
  lastName: yup.string().required().label('Last Name'),
  email: yup.string().email().label('Email').when(['isKeyContact'], {
    is: true,
    then: yup.string().email().required().label('Email')
  }).test('invalid-newfront-email', "Email can't be from Newfront", emailIsNotFromNewfront),
  website: yup.string().label('Website').matches(WEB_URL_REGEXP, {
    message: 'Invalid URL',
    excludeEmptyString: true
  })
});

function emailIsNotFromNewfront(value) {
  return !(value !== null && value !== void 0 && value.includes('@newfront.com'));
}

export { AccountSwitcher as A, AccountCard as a, buildIdentitiesDefinitions as b, contactValidationSchema as c, getHumanReadableCorrespondence as g, isPublicAccount as i, useTeamMemberColumns as u };
